'use client'

import { createContext, useContext, useEffect, useState, ReactNode } from 'react'
import { performanceManager } from '@/lib/performance'

type Theme = 'light' | 'dark'

interface ThemeContextType {
  theme: Theme
  toggleTheme: () => void
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined)

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [theme, setTheme] = useState<Theme>('light')
  const [mounted, setMounted] = useState(false)

  useEffect(() => {
    // Add preload class to prevent transitions on initial load
    document.documentElement.classList.add('preload')
    
    // Check for saved theme in localStorage or system preference
    const savedTheme = localStorage.getItem('theme') as Theme | null
    const systemPrefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches
    
    // Set the initial theme
    const initialTheme = savedTheme || (systemPrefersDark ? 'dark' : 'light')
    setTheme(initialTheme)
    
    // Update both data-theme attribute and class for Tailwind compatibility
    document.documentElement.setAttribute('data-theme', initialTheme)
    if (initialTheme === 'dark') {
      document.documentElement.classList.add('dark')
    } else {
      document.documentElement.classList.remove('dark')
    }
    
    // Remove preload class after a short delay to enable transitions
    const preloadTimer = setTimeout(() => {
      document.documentElement.classList.remove('preload')
    }, 100)
    
    setMounted(true)
    
    // Listen for system theme changes with optimized handler
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)')
    const handleChange = () => {
      if (!savedTheme) { // Only update if user hasn't set a preference
        const newTheme = mediaQuery.matches ? 'dark' : 'light'
        setTheme(newTheme)
        document.documentElement.setAttribute('data-theme', newTheme)
        if (newTheme === 'dark') {
          document.documentElement.classList.add('dark')
        } else {
          document.documentElement.classList.remove('dark')
        }
      }
    }
    
    mediaQuery.addEventListener('change', handleChange)
    
    // Cleanup function for memory management
    return () => {
      clearTimeout(preloadTimer)
      mediaQuery.removeEventListener('change', handleChange)
      // Clean up any remaining transition classes
      document.documentElement.classList.remove('theme-transitioning', 'theme-transition-complete', 'preload')
    }
  }, [])

  useEffect(() => {
    if (!mounted) return
    
    // Save theme to localStorage and update the data-theme attribute
    localStorage.setItem('theme', theme)
    document.documentElement.setAttribute('data-theme', theme)
  }, [theme, mounted])

  const toggleTheme = () => {
    // Start performance monitoring
    performanceManager.startThemeTransition()
    
    // Add transitioning class for performance optimization
    document.documentElement.classList.add('theme-transitioning')
    
    setTheme(prevTheme => {
      const newTheme = prevTheme === 'light' ? 'dark' : 'light'
      
      // Update theme attributes
      document.documentElement.setAttribute('data-theme', newTheme)
      if (newTheme === 'dark') {
        document.documentElement.classList.add('dark')
      } else {
        document.documentElement.classList.remove('dark')
      }
      
      // Save to localStorage
      localStorage.setItem('theme', newTheme)
      
      // Remove transitioning class after transition completes
      setTimeout(() => {
        document.documentElement.classList.remove('theme-transitioning')
        document.documentElement.classList.add('theme-transition-complete')
        
        // End performance monitoring
        performanceManager.endThemeTransition()
        
        // Remove complete class after a short delay
        setTimeout(() => {
          document.documentElement.classList.remove('theme-transition-complete')
        }, 50)
      }, 250) // Match transition duration
      
      return newTheme
    })
  }

  // Prevent rendering until theme is determined to avoid flash of incorrect theme
  if (!mounted) {
    return null
  }

  return (
    <ThemeContext.Provider value={{ theme, toggleTheme }}>
      {children}
    </ThemeContext.Provider>
  )
}

export function useTheme() {
  const context = useContext(ThemeContext)
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider')
  }
  return context
}